import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import salesApiClient from '@/api/salesClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogOverlay } from '@/components/ui/dialog';
import { Plus, ArrowRight, LayoutGrid, List, Search, Kanban } from 'lucide-react';

export default function Leads() {
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState(searchParams.get('view') === 'pipeline' ? 'pipeline' : 'list'); // 'grid', 'list' or 'pipeline'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [formData, setFormData] = useState({
    contact_id: '',
    stage: 'prospect',
    value: '',
    requirements: '',
    notes: '',
    contract_type: 'staffing',
    duration_months: '12',
  });

  const stages = ['prospect', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];

  useEffect(() => {
    fetchLeads();
    fetchContacts();
    fetchUsers();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await salesApiClient.get('/leads');
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to fetch leads');
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await salesApiClient.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await salesApiClient.get('/team/members');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const getFilteredLeads = () => {
    return leads.filter(lead => {
      const contact = contacts.find(c => c.id === lead.contact_id);
      const matchesSearch = searchTerm === '' || 
        (contact?.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contact?.company?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.requirements?.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesUser = filterUser === '' || lead.assigned_to === filterUser || lead.owner_id === filterUser;
      const matchesStage = filterStage === '' || lead.stage === filterStage;
      
      // Date filtering
      let matchesDate = true;
      if (filterDateFrom || filterDateTo) {
        const leadDate = new Date(lead.created_at);
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          matchesDate = matchesDate && leadDate >= fromDate;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          matchesDate = matchesDate && leadDate <= toDate;
        }
      }
      
      return matchesSearch && matchesUser && matchesDate && matchesStage;
    });
  };
  
  const getLeadsByStage = (stage) => {
    return getFilteredLeads().filter(lead => lead.stage === stage);
  };

  const getPipelineValue = (stage) => {
    return getLeadsByStage(stage).reduce((sum, lead) => sum + (lead.value || 0), 0);
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unassigned';
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'prospect': return 'bg-slate-500/20 text-slate-400';
      case 'qualified': return 'bg-blue-500/20 text-blue-400';
      case 'proposal': return 'bg-amber-500/20 text-amber-400';
      case 'negotiation': return 'bg-purple-500/20 text-purple-400';
      case 'closed_won': return 'bg-green-500/20 text-green-400';
      case 'closed_lost': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await salesApiClient.post('/leads', {
        ...formData,
        value: parseFloat(formData.value),
        duration_months: formData.duration_months ? parseInt(formData.duration_months) : null,
      });
      toast.success('Lead created successfully');
      setIsDialogOpen(false);
      setFormData({ contact_id: '', stage: 'prospect', value: '', requirements: '', notes: '', contract_type: 'staffing', duration_months: '12' });
      fetchLeads();
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await salesApiClient.patch(`/leads/${leadId}`, { stage: newStage });
      toast.success('Lead stage updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const getContactName = (contactId) => {
    const contact = contacts.find((c) => c.id === contactId);
    return contact ? `${contact.name} (${contact.company})` : 'Unknown';
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white" data-testid="leads-title">
            Sales Pipeline
          </h1>
          <p className="text-base text-slate-400 mt-2">Track and manage your opportunities</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-lead-button">
              <Plus size={20} className="mr-2" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogOverlay className="bg-black/80" />
          <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="add-lead-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Lead</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="contact">Contact</Label>
                <select
                  id="contact"
                  value={formData.contact_id}
                  onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 text-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                  data-testid="lead-contact-select"
                >
                  <option value="">Select a contact</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.name} - {contact.company}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-200" htmlFor="contract_type">Type</Label>
                  <select
                    id="contract_type"
                    value={formData.contract_type}
                    onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 text-white px-3 py-2 text-sm"
                    data-testid="lead-contract-type-select"
                  >
                    <option value="staffing">Contract Staffing</option>
                    <option value="project">Adhoc Project</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-200" htmlFor="duration">Duration (months)</Label>
                  <Input className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    id="duration"
                    type="number"
                    value={formData.duration_months}
                    onChange={(e) => setFormData({ ...formData, duration_months: e.target.value })}
                    placeholder="12"
                    data-testid="lead-duration-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="value">Deal Value ($)</Label>
                <Input className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  id="value"
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder="50000"
                  required
                  data-testid="lead-value-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="requirements">Requirements</Label>
                <Textarea className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  id="requirements"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  placeholder="5 Senior React developers, 3 DevOps engineers..."
                  required
                  data-testid="lead-requirements-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200" htmlFor="notes">Notes</Label>
                <Textarea className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  data-testid="lead-notes-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-lead-button">
                Create Lead
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input 
            placeholder="Search leads..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          className="h-10 rounded-md border border-slate-700 bg-slate-800 text-white px-3 text-sm"
        >
          <option value="">All Sales Reps</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        <select
          value={filterStage}
          onChange={(e) => setFilterStage(e.target.value)}
          className="h-10 rounded-md border border-slate-700 bg-slate-800 text-white px-3 text-sm"
        >
          <option value="">All Stages</option>
          <option value="prospect">Prospect</option>
          <option value="qualified">Qualified</option>
          <option value="proposal">Proposal</option>
          <option value="negotiation">Negotiation</option>
          <option value="closed_won">Closed Won</option>
          <option value="closed_lost">Closed Lost</option>
        </select>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">From:</span>
          <Input 
            type="date" 
            value={filterDateFrom} 
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">To:</span>
          <Input 
            type="date" 
            value={filterDateTo} 
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        {(filterUser || filterDateFrom || filterDateTo || filterStage) && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setFilterUser('');
              setFilterDateFrom('');
              setFilterDateTo('');
              setFilterStage('');
            }}
            className="text-slate-400 hover:text-white"
          >
            Clear Filters
          </Button>
        )}
        <div className="flex bg-slate-800 rounded-lg p-1">
          <Button size="sm" variant={viewMode === 'pipeline' ? 'default' : 'ghost'} onClick={() => setViewMode('pipeline')} title="Pipeline View">
            <Kanban size={18} />
          </Button>
          <Button size="sm" variant={viewMode === 'grid' ? 'default' : 'ghost'} onClick={() => setViewMode('grid')} title="Grid View">
            <LayoutGrid size={18} />
          </Button>
          <Button size="sm" variant={viewMode === 'list' ? 'default' : 'ghost'} onClick={() => setViewMode('list')} title="List View">
            <List size={18} />
          </Button>
        </div>
      </div>

      {/* Pipeline View (Kanban Board) */}
      {viewMode === 'pipeline' && (
        <div className="overflow-x-auto">
          <div className="flex gap-4 min-w-max pb-4" data-testid="pipeline-board">
            {stages.map((stage) => {
              const stageLeads = getLeadsByStage(stage);
              const stageValue = getPipelineValue(stage);
              
              return (
                <div key={stage} className="flex-shrink-0 w-72" data-testid={`pipeline-stage-${stage}`}>
                  <Card className="bg-slate-800 border-slate-700 rounded-xl p-4 h-full">
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">
                        {stage.replace('_', ' ')}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1">
                        {stageLeads.length} leads • ${stageValue.toLocaleString()}
                      </p>
                    </div>
                    <div className="space-y-3">
                      {stageLeads.map((lead) => {
                        const contact = contacts.find(c => c.id === lead.contact_id);
                        return (
                          <Card key={lead.id} className="bg-slate-900 border-slate-700 p-3 rounded-lg cursor-pointer hover:border-slate-600 transition-all">
                            <p className="text-sm font-medium text-white">{contact?.company || 'Unknown'}</p>
                            <p className="text-xs text-slate-400 mt-1">{contact?.name}</p>
                            <p className="text-sm font-semibold text-green-400 mt-2">${(lead.value || 0).toLocaleString()}</p>
                          </Card>
                        );
                      })}
                      {stageLeads.length === 0 && (
                        <p className="text-xs text-slate-500 text-center py-4">No leads</p>
                      )}
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Grid View - Card Layout */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {getFilteredLeads().map((lead) => {
            const contact = contacts.find(c => c.id === lead.contact_id);
            return (
              <Card key={lead.id} className="bg-slate-900 border border-slate-800 rounded-xl p-6 hover:border-slate-600 transition-all">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-lg font-semibold text-white">{contact?.company || 'Unknown'}</p>
                    <p className="text-sm text-slate-400">{contact?.name}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${getStageColor(lead.stage)}`}>
                    {lead.stage?.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-2xl font-bold text-green-400 mb-2">${(lead.value || 0).toLocaleString()}</p>
                <p className="text-sm text-slate-400 line-clamp-2 mb-3">{lead.requirements}</p>
                <div className="flex justify-between items-center text-xs text-slate-500">
                  <span>By: {getUserName(lead.assigned_to || lead.owner_id)}</span>
                  {!lead.stage?.includes('closed') && (
                    <Button size="sm" variant="outline" onClick={() => {
                      const idx = stages.indexOf(lead.stage);
                      if (idx < stages.length - 1) handleStageChange(lead.id, stages[idx + 1]);
                    }}>
                      Next <ArrowRight size={14} className="ml-1" />
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="bg-slate-900 border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Contact/Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Value</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Stage</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Sales Rep</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Requirements</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {getFilteredLeads().map(lead => (
                <tr key={lead.id} className="hover:bg-slate-800/50">
                  <td className="px-4 py-3 text-white font-medium">{getContactName(lead.contact_id)}</td>
                  <td className="px-4 py-3 text-primary font-bold">RM {(lead.value || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      lead.stage === 'closed_won' ? 'bg-green-500/20 text-green-400' :
                      lead.stage === 'closed_lost' ? 'bg-red-500/20 text-red-400' :
                      lead.stage === 'negotiation' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {lead.stage?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-cyan-400 text-sm">{getUserName(lead.assigned_to || lead.owner_id)}</td>
                  <td className="px-4 py-3 text-slate-400 text-sm max-w-xs truncate">{lead.requirements}</td>
                  <td className="px-4 py-3">
                    {!lead.stage?.includes('closed') && (
                      <Button size="sm" variant="outline" onClick={() => {
                        const idx = stages.indexOf(lead.stage);
                        if (idx < stages.length - 1) handleStageChange(lead.id, stages[idx + 1]);
                      }}>
                        Next <ArrowRight size={14} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {getFilteredLeads().length === 0 && (
            <div className="p-8 text-center text-slate-400">No leads found</div>
          )}
        </Card>
      )}

      {leads.length === 0 && viewMode === 'grid' && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-12 text-center" data-testid="no-leads">
          <p className="text-slate-400">No leads yet. Add your first lead to get started.</p>
        </Card>
      )}
    </div>
  );
}
